import { useRef, useState, type ReactNode, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminAdInsights } from "@/lib/admin.functions";
import { AdminAdFacts, useIsAdmin } from "@/components/AdminAdInfo";
import { AdminInfoSheet } from "@/components/admin/AdminInfoSheet";
import { useHydrated } from "@/hooks/useHydrated";
import { useIsTouch } from "@/hooks/useIsTouch";

/**
 * Adminbubbla vid annonskort. Med mus följer den pekaren; på pekskärm (mobil,
 * platta och appen) visas samma info i en panel längst ner efter ett långt
 * tryck – hover-bubblan skulle annars fastna och bryta layouten.
 */
export function AdminAdTooltip({
  slug,
  referenceSlug,
  children,
}: {
  slug: string;
  referenceSlug?: string | null;
  children: ReactNode;
}) {
  const isAdmin = useIsAdmin();
  const hydrated = useHydrated();
  const touch = useIsTouch();
  const [hovered, setHovered] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchInsights = useServerFn(adminAdInsights);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-ad-insights", slug, referenceSlug ?? null],
    queryFn: () => fetchInsights({ data: { slug, referenceSlug: referenceSlug ?? null } }),
    enabled: isAdmin && (hovered || sheet),
    staleTime: 60 * 1000,
  });

  if (!isAdmin) return <>{children}</>;

  const WIDTH = 300;
  const track = (e: MouseEvent) => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const x = Math.min(e.clientX + 16, vw - WIDTH - 12);
    const y = Math.max(12, e.clientY - 12);
    setPos({ x, y });
  };
  const cancelPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  const facts =
    isLoading || !data ? (
      <p className="mt-1 text-muted-foreground">Hämtar…</p>
    ) : (
      <div className="mt-1 [&_p]:break-words">
        <AdminAdFacts data={data} />
      </div>
    );

  if (touch) {
    return (
      <div
        className="contents"
        onTouchStart={() => {
          cancelPress();
          pressTimer.current = setTimeout(() => setSheet(true), 550);
        }}
        onTouchMove={cancelPress}
        onTouchEnd={cancelPress}
        onTouchCancel={cancelPress}
      >
        {children}
        <AdminInfoSheet open={hydrated && sheet} onClose={() => setSheet(false)}>
          {facts}
        </AdminInfoSheet>
      </div>
    );
  }

  return (
    <div
      className="contents"
      onMouseEnter={(e) => {
        track(e);
        setHovered(true);
      }}
      onMouseMove={track}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {hydrated &&
        hovered &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[100] rounded-lg border border-border bg-popover p-3 text-xs leading-relaxed text-popover-foreground shadow-xl"
            style={{ left: pos.x, top: pos.y, width: WIDTH, transform: "translateY(-100%)" }}
          >
            <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
              Admininfo
            </p>
            {facts}
          </div>,
          document.body,
        )}
    </div>
  );
}
